import React, { useState, useEffect, useRef } from 'react'
import DateRangePicker from './DateRangePicker'
import ProjectTaskSelectionModal from './modals/ProjectTaskSelectionModal'
import AddWorklogModal from './modals/AddWorklogModal'
import MultipleWorklogEntriesModal from './modals/MultipleWorklogEntriesModal'
import apiClient from '../utils/api'
import styles from './TimesheetTable.module.css'
import { formatDisplayDate, getWeekStart, getWeekEnd, formatWeekRange, moment } from '../utils/dateUtils'

export default function TimesheetTable({ availableProjects = [], availableTasks = [], apiToken, user }) {
    const [dateRange, setDateRange] = useState({
        from: '',
        to: '',
        preset: 'This week'
    })
    
    const [timesheetRows, setTimesheetRows] = useState([])
    const [worklogData, setWorklogData] = useState([])
    const [pendingWorklogs, setPendingWorklogs] = useState([]) // Track worklogs created but not yet in worklogData
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)

    // Modal state for project/task selection
    const [isProjectModalOpen, setIsProjectModalOpen] = useState(false)
    const [currentEditingRowId, setCurrentEditingRowId] = useState(null)

    // Modal state for worklog edit/create
    const [isWorklogModalOpen, setIsWorklogModalOpen] = useState(false)
    const [currentWorklogData, setCurrentWorklogData] = useState(null)
    const [isEditingWorklog, setIsEditingWorklog] = useState(false)
    const [currentUser, setCurrentUser] = useState(null)

    // Modal state for multiple worklog entries
    const [isMultipleEntriesModalOpen, setIsMultipleEntriesModalOpen] = useState(false)
    const [multipleWorklogEntries, setMultipleWorklogEntries] = useState([])

    // Teammates state
    const [teammates, setTeammates] = useState([])
    const [selectedTeammate, setSelectedTeammate] = useState(null)
    const [loadingTeammates, setLoadingTeammates] = useState(false)
    const [isAdmin, setIsAdmin] = useState(false)
    const [copyingLastWeek, setCopyingLastWeek] = useState(false)

    // Use fallback mock data if no props provided
    const projects = availableProjects.length > 0 ? availableProjects : []
    const tasks = availableTasks

    // Initialize date range on mount
    useEffect(() => {
        const today = moment()
        const startOfWeek = today.clone().startOf('isoWeek') // Monday
        const endOfWeek = startOfWeek.clone().endOf('isoWeek') // Sunday

        setDateRange({
            from: startOfWeek.format('YYYY-MM-DD'),
            to: endOfWeek.format('YYYY-MM-DD'),
            preset: 'This week'
        })
    }, [])

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (event) => {
            // Check if focus is on a time input to avoid interfering with time entry
            if (event.target.classList.contains(styles.timeInput)) {
                return
            }

            if (event.key === 'Delete' && timesheetRows.length > 0) {
                // Remove the last row when Delete is pressed
                const lastRow = timesheetRows[timesheetRows.length - 1]
                if (lastRow) {
                    removeRow(lastRow.id)
                }
                event.preventDefault()
            }
        }

        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [timesheetRows])

    // Fetch teammates on mount (only for admins)
    useEffect(() => {
        const loadTeammates = async () => {
            if (!apiToken) return
            
            try {
                setLoadingTeammates(true)
                apiClient.setAuthToken(apiToken)
                
                const data = await apiClient.getTeammates()
                setTeammates(data.teammates || [])
                setIsAdmin(true) // If the request succeeds, user is admin
            } catch (error) {
                console.error('Error fetching teammates:', error)
                if (error.message.includes('403') || error.message.includes('Admin access required')) {
                    setIsAdmin(false)
                } else {
                    console.error('Failed to load teammates:', error)
                }
            } finally {
                setLoadingTeammates(false)
            }
        }
        
        loadTeammates()
    }, [apiToken])

    // Function to fetch timesheet data
    const fetchTimesheetData = async (userId = null) => {
        if (!apiToken || !dateRange.from || !dateRange.to) {
            return
        }

        try {
            setLoading(true)
            setError(null)
            apiClient.setAuthToken(apiToken)

            // Use reports API to get worklog data for the date range
            // Default to current user's ID if no userId is provided
            const targetUserId = userId || user?.id

            const params = {
                start_date: dateRange.from,
                end_date: dateRange.to,
                team: targetUserId // Always filter by specific user
            }

            // Build query string manually to ensure proper formatting
            const queryParams = new URLSearchParams()
            queryParams.append('start_date', params.start_date)
            queryParams.append('end_date', params.end_date)
            if (params.team) {
                queryParams.append('team', params.team)
            }

            const response = await apiClient.get(`/reports?${queryParams.toString()}`)
            const worklogs = response?.detailed?.data || []

            setWorklogData(worklogs)
            setPendingWorklogs([]) // Clear pending worklogs now that we have fresh data
            processWorklogsIntoTimesheet(worklogs)

        } catch (error) {
            console.error('Failed to fetch timesheet data:', error)
            setError(error.message)
        } finally {
            setLoading(false)
        }
    }

    // Function to process worklogs into timesheet format
    const processWorklogsIntoTimesheet = (worklogs) => {
        const projectGroups = {}
        
        // Group worklogs by project/task combination
        worklogs.forEach(worklog => {
            const projectKey = worklog.project ? 
                (worklog.task ? `${worklog.project.name} > ${worklog.task.name}` : worklog.project.name) :
                'No Project'
            
            if (!projectGroups[projectKey]) {
                // Determine billable value: task > project > default true
                let billableValue = true
                if (worklog.task?.is_billable !== undefined) {
                    billableValue = worklog.task.is_billable
                } else if (worklog.project?.is_billable !== undefined) {
                    billableValue = worklog.project.is_billable
                }

                projectGroups[projectKey] = {
                    id: worklog.project?.id || `no-project-${Date.now()}`,
                    selectedProject: worklog.project,
                    selectedTask: worklog.task,
                    billable: billableValue,
                    isSelectingProject: !worklog.project,
                    times: {
                        'Mon': '00:00:00',
                        'Tue': '00:00:00',
                        'Wed': '00:00:00',
                        'Thu': '00:00:00',
                        'Fri': '00:00:00',
                        'Sat': '00:00:00',
                        'Sun': '00:00:00'
                    },
                    editingValues: {},
                    isEditing: {}
                }
            }
            
            // Add duration to appropriate day
            const worklogDate = moment(worklog.date)
            const dayOfWeek = worklogDate.day() // 0 = Sunday, 1 = Monday, etc.
            const dayKeys = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
            const dayKey = dayKeys[dayOfWeek]
            
            // Convert duration from seconds to HH:MM:SS format
            const duration = worklog.duration_seconds || 0
            const existingTime = projectGroups[projectKey].times[dayKey]
            const existingSeconds = timeStringToSeconds(existingTime)
            const totalSeconds = existingSeconds + duration
            
            projectGroups[projectKey].times[dayKey] = secondsToTimeString(totalSeconds)
        })
        
        // Convert to array and add empty row for new entries
        const rows = Object.keys(projectGroups).map(key => {
            const group = projectGroups[key]
            // Generate unique ID combining project and task (if present)
            const uniqueId = group.selectedProject
                ? (group.selectedTask
                    ? `${group.selectedProject.id}-${group.selectedTask.id}`
                    : group.selectedProject.id)
                : `row-${Date.now()}-${Math.random()}`

            return {
                ...group,
                id: uniqueId
            }
        })
        
        // Add empty row for new entries if no data exists
        if (rows.length === 0) {
            rows.push({
                id: `empty-${Date.now()}`,
                selectedProject: null,
                selectedTask: null,
                isSelectingProject: true,
                times: {
                    'Mon': '00:00:00',
                    'Tue': '00:00:00',
                    'Wed': '00:00:00',
                    'Thu': '00:00:00',
                    'Fri': '00:00:00',
                    'Sat': '00:00:00',
                    'Sun': '00:00:00'
                },
                editingValues: {},
                isEditing: {}
            })
        }
        
        setTimesheetRows(rows)
    }

    // Helper functions for time conversion
    const timeStringToSeconds = (timeString) => {
        const [hours, minutes, seconds] = timeString.split(':').map(Number)
        return hours * 3600 + minutes * 60 + seconds
    }
    
    const secondsToTimeString = (totalSeconds) => {
        const hours = Math.floor(totalSeconds / 3600)
        const minutes = Math.floor((totalSeconds % 3600) / 60)
        const seconds = totalSeconds % 60
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    }

    // Load timesheet data when date range or selected teammate changes
    useEffect(() => {
        fetchTimesheetData(selectedTeammate?.id)
    }, [apiToken, dateRange.from, dateRange.to, selectedTeammate?.id])

    // Modal handlers
    const handleOpenProjectModal = (rowId) => {
        setCurrentEditingRowId(rowId)
        setIsProjectModalOpen(true)
    }

    const handleCloseProjectModal = () => {
        setIsProjectModalOpen(false)
        setCurrentEditingRowId(null)
    }

    const handleProjectTaskSelection = (selection) => {
        if (currentEditingRowId) {
            // Capture old project/task before updating row state
            const currentRow = timesheetRows.find(r => r.id === currentEditingRowId)
            const oldProject = currentRow?.selectedProject
            const oldTask = currentRow?.selectedTask

            setTimesheetRows(timesheetRows.map(row => {
                if (row.id === currentEditingRowId) {
                    // Generate new unique ID based on project+task selection
                    const newId = selection.project
                        ? (selection.task
                            ? `${selection.project.id}-${selection.task.id}`
                            : selection.project.id)
                        : row.id // Keep existing ID if no project selected

                    return {
                        ...row,
                        id: newId,
                        selectedProject: selection.project,
                        selectedTask: selection.task,
                        billable: selection.billable,
                        isSelectingProject: !selection.project
                    }
                }
                return row
            }))

            // Update currentEditingRowId to the new ID
            if (selection.project) {
                const newId = selection.task
                    ? `${selection.project.id}-${selection.task.id}`
                    : selection.project.id
                setCurrentEditingRowId(newId)
            }

            // If project/task changed on a row that had existing worklogs, update them
            if (oldProject && selection.project &&
                (oldProject.id !== selection.project.id || (oldTask?.id || null) !== (selection.task?.id || null))) {
                const matchingWorklogs = worklogData.filter(worklog => {
                    const isSameProject = worklog.project?.id === oldProject.id
                    const isSameTask = oldTask
                        ? worklog.task?.id === oldTask.id
                        : !worklog.task
                    return isSameProject && isSameTask
                })

                if (matchingWorklogs.length > 0) {
                    apiClient.setAuthToken(apiToken)
                    Promise.all(
                        matchingWorklogs.map(worklog =>
                            apiClient.updateWorklog(worklog.id, {
                                project: selection.project.id,
                                task: selection.task?.id || null,
                                billable: selection.billable
                            })
                        )
                    ).then(() => {
                        fetchTimesheetData(selectedTeammate?.id)
                    }).catch((error) => {
                        console.error('Failed to update worklogs:', error)
                        alert('Failed to update worklogs with new project/task: ' + (error.message || 'Unknown error'))
                        fetchTimesheetData(selectedTeammate?.id)
                    })
                }
            }
        }
    }

    const getCurrentRowSelections = () => {
        if (!currentEditingRowId) return { project: null, task: null, billable: true }
        const row = timesheetRows.find(r => r.id === currentEditingRowId)
        return {
            project: row?.selectedProject || null,
            task: row?.selectedTask || null,
            billable: row?.billable !== undefined ? row.billable : true
        }
    }

    const handleTeammateChange = (e) => {
        const teammateId = e.target.value
        if (teammateId === '') {
            setSelectedTeammate(null)
        } else {
            const teammate = teammates.find(t => t.id === teammateId)
            setSelectedTeammate(teammate)
        }
        // The useEffect will automatically fetch new timesheet data when selectedTeammate changes
    }

    // Helper function to check if a cell has multiple entries
    const hasMultipleEntries = (row, day) => {
        if (!row.selectedProject) return false

        const weekStart = moment(dateRange.from)
        const dayIndex = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].indexOf(day)
        const cellDate = weekStart.clone().add(dayIndex, 'days')

        const matchingWorklogs = worklogData.filter(worklog => {
            const worklogDate = moment(worklog.date)
            const isSameDate = worklogDate.isSame(cellDate, 'day')
            const isSameProject = worklog.project?.id === row.selectedProject?.id
            const isSameTask = row.selectedTask
                ? worklog.task?.id === row.selectedTask?.id
                : !worklog.task

            return isSameDate && isSameProject && isSameTask
        })

        return matchingWorklogs.length > 1
    }

    // Worklog modal handlers
    const handleTimeCellClick = async (row, day) => {
        // Don't open modal if no project is selected
        if (!row.selectedProject) {
            alert('Please select a project first')
            return
        }

        // Get the date for the clicked cell
        const weekStart = moment(dateRange.from)
        const dayIndex = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].indexOf(day)
        const cellDate = weekStart.clone().add(dayIndex, 'days')

        // Find existing worklogs for this project/task/date combination
        const matchingWorklogs = worklogData.filter(worklog => {
            const worklogDate = moment(worklog.date)
            const isSameDate = worklogDate.isSame(cellDate, 'day')
            const isSameProject = worklog.project?.id === row.selectedProject?.id
            const isSameTask = row.selectedTask
                ? worklog.task?.id === row.selectedTask?.id
                : !worklog.task

            return isSameDate && isSameProject && isSameTask
        })

        if (matchingWorklogs.length > 1) {
            // Multiple entries found - show the multiple entries modal
            setMultipleWorklogEntries(matchingWorklogs)
            setIsMultipleEntriesModalOpen(true)
        } else if (matchingWorklogs.length === 1) {
            // Single entry found - open the single worklog modal for editing
            const worklog = matchingWorklogs[0]
            setCurrentWorklogData({
                id: worklog.id,
                title: worklog.description || '',
                startTime: worklog.start_time,
                endTime: worklog.end_time,
                date: cellDate.format('YYYY-MM-DD'),
                project: worklog.project,
                task: worklog.task,
                userId: worklog.user_id,
                billable: worklog.billable
            })
            setIsEditingWorklog(true)
            setIsWorklogModalOpen(true)
        } else {
            // No existing worklog, create new one
            // Pre-fill with project, task, and date
            // Use the billable value stored in the row (from ProjectTaskSelectionModal)
            const billableValue = row.billable !== undefined ? row.billable : true

            setCurrentWorklogData({
                title: '',
                startTime: null,
                endTime: null,
                date: cellDate.format('YYYY-MM-DD'),
                project: row.selectedProject,
                task: row.selectedTask,
                // Don't pass userId - backend will use authenticated user
                billable: billableValue
            })
            setIsEditingWorklog(false)
            setIsWorklogModalOpen(true)
        }
    }

    const handleCloseWorklogModal = () => {
        setIsWorklogModalOpen(false)
        setCurrentWorklogData(null)
        setIsEditingWorklog(false)
    }

    // Multiple entries modal handlers
    const handleCloseMultipleEntriesModal = () => {
        setIsMultipleEntriesModalOpen(false)
        setMultipleWorklogEntries([])
    }

    const handleSaveMultipleEntry = async (worklogData) => {
        await handleSaveWorklog(worklogData)
        // Refresh the entries after saving
        // The modal will update automatically through the worklogData state refresh
    }

    const handleDeleteMultipleEntry = async (worklogId) => {
        await handleDeleteWorklog(worklogId)
        // Close the multiple entries modal if all entries are deleted
        // This will be handled by the modal component itself
    }

    const handleSaveWorklog = async (worklogData) => {
        try {
            apiClient.setAuthToken(apiToken)

            // Prepare API payload
            const payload = {
                worklog: {
                    description: worklogData.title,
                    start_time: moment(worklogData.startTime).toISOString(),
                    end_time: moment(worklogData.endTime).toISOString(),
                    logged_date: moment(worklogData.date).format('YYYY-MM-DD'),
                    project: worklogData.project?.id || worklogData.project,
                    task: worklogData.task?.id || worklogData.task || null,
                    // Don't pass user_id - backend will use authenticated user
                    billable: worklogData.billable
                }
            }

            // Determine if this is an update or create based on worklog ID
            if (worklogData.id) {
                // Update existing worklog
                await apiClient.updateWorklog(worklogData.id, payload.worklog)
            } else {
                // Create new worklog
                await apiClient.createWorklog(payload.worklog)
            }

            // Refresh timesheet data
            await fetchTimesheetData(selectedTeammate?.id)

            handleCloseWorklogModal()
        } catch (error) {
            console.error('Failed to save worklog:', error)
            alert('Failed to save worklog: ' + (error.message || 'Unknown error'))
        }
    }

    const handleDeleteWorklog = async (worklogId) => {
        try {
            apiClient.setAuthToken(apiToken)
            await apiClient.deleteWorklog(worklogId)

            // Refresh timesheet data
            await fetchTimesheetData(selectedTeammate?.id)

            handleCloseWorklogModal()
        } catch (error) {
            console.error('Failed to delete worklog:', error)
            alert('Failed to delete worklog: ' + (error.message || 'Unknown error'))
        }
    }

    // Get week dates for header
    const getWeekDates = () => {
        if (!dateRange.from) return []

        const startDate = moment(dateRange.from)
        const dates = []

        for (let i = 0; i < 7; i++) {
            const date = startDate.clone().add(i, 'days')
            dates.push({
                day: date.format('ddd'),
                date: date.format('MMM D')
            })
        }

        return dates
    }

    const addNewRow = () => {
        const newRow = {
            id: `new-row-${Date.now()}`,
            selectedProject: null,
            selectedTask: null,
            isSelectingProject: true,
            times: {
                'Mon': '00:00:00',
                'Tue': '00:00:00',
                'Wed': '00:00:00', 
                'Thu': '00:00:00',
                'Fri': '00:00:00',
                'Sat': '00:00:00',
                'Sun': '00:00:00'
            },
            editingValues: {},
            isEditing: {},
            isNewRow: true // Flag to indicate this is a manually added row
        }
        setTimesheetRows([...timesheetRows, newRow])
    }

    const removeRow = (rowId) => {
        const rowToRemove = timesheetRows.find(row => row.id === rowId)
        
        // Check if the row has any logged time
        const hasLoggedTime = rowToRemove && Object.values(rowToRemove.times).some(time => time !== '00:00:00')
        
        // Show confirmation if there's logged time or if it's the last row
        if (hasLoggedTime) {
            const projectName = rowToRemove.selectedProject?.name || 'this project'
            const confirmMessage = `Are you sure you want to remove ${projectName}? This will delete all logged time for this row.`
            
            if (!window.confirm(confirmMessage)) {
                return
            }
        } else if (timesheetRows.length === 1) {
            // If it's the last row with no time, reset it instead of removing
            const resetRow = {
                id: rowToRemove.id,
                selectedProject: null,
                selectedTask: null,
                isSelectingProject: true,
                times: {
                    'Mon': '00:00:00',
                    'Tue': '00:00:00',
                    'Wed': '00:00:00',
                    'Thu': '00:00:00',
                    'Fri': '00:00:00',
                    'Sat': '00:00:00',
                    'Sun': '00:00:00'
                },
                editingValues: {},
                isEditing: {}
            }
            setTimesheetRows([resetRow])
            return
        }
        
        // Remove the row
        setTimesheetRows(timesheetRows.filter(row => row.id !== rowId))
    }

    const getProjectButtonText = (row) => {
        if (row.selectedProject && row.selectedTask) {
            return `${row.selectedProject.name} > ${row.selectedTask.name}`
        } else if (row.selectedProject) {
            return row.selectedProject.name
        }
        return 'select a project'
    }

    const hasLoggedTime = (row) => {
        return Object.values(row.times).some(time => time !== '00:00:00')
    }

    const updateTime = (rowId, day, time) => {
        setTimesheetRows(timesheetRows.map(row =>
            row.id === rowId
                ? { ...row, editingValues: { ...row.editingValues, [day]: time } }
                : row
        ))
    }

    const formatTimeInput = (input) => {
        if (!input || input.trim() === '') return '00:00:00'
        
        // Remove any non-digit and non-colon characters
        const cleaned = input.replace(/[^\d:]/g, '')
        
        // Handle different input formats
        if (cleaned.includes(':')) {
            const parts = cleaned.split(':')
            if (parts.length === 2) {
                // Format: "56:15" -> "56:15:00"
                const hours = parseInt(parts[0]) || 0
                const minutes = parseInt(parts[1]) || 0
                return `${hours.toString().padStart(2, '0')}:${Math.min(minutes, 59).toString().padStart(2, '0')}:00`
            } else if (parts.length === 3) {
                // Format: "56:15:30" -> "56:15:30"
                const hours = parseInt(parts[0]) || 0
                const minutes = parseInt(parts[1]) || 0
                const seconds = parseInt(parts[2]) || 0
                return `${hours.toString().padStart(2, '0')}:${Math.min(minutes, 59).toString().padStart(2, '0')}:${Math.min(seconds, 59).toString().padStart(2, '0')}`
            }
        } else {
            // Handle pure numbers without colons
            const number = parseInt(cleaned) || 0
            const numStr = cleaned.toString()
            
            if (numStr.length === 1) {
                // Single digit: "5" -> "05:00:00"
                return `${number.toString().padStart(2, '0')}:00:00`
            } else if (numStr.length === 2) {
                // Two digits: "85" -> "08:05:00" (first digit = hours, second digit = single minutes)
                const hours = parseInt(numStr[0])
                const minutes = parseInt(numStr[1])
                return `${hours.toString().padStart(2, '0')}:${(minutes).toString().padStart(2, '0')}:00`
            } else if (numStr.length === 3) {
                // Three digits: "430" -> "04:30:00" (treat as HMM format)
                const hours = Math.floor(number / 100)
                const minutes = number % 100
                return `${hours.toString().padStart(2, '0')}:${Math.min(minutes, 59).toString().padStart(2, '0')}:00`
            } else if (numStr.length === 4) {
                // Four digits: "1430" -> "14:30:00" (treat as HHMM format)
                const hours = Math.floor(number / 100)
                const minutes = number % 100
                return `${hours.toString().padStart(2, '0')}:${Math.min(minutes, 59).toString().padStart(2, '0')}:00`
            } else if (numStr.length === 5) {
                // Five digits: "14305" -> "14:30:05" (treat as HHMMS format)
                const hours = Math.floor(number / 1000)
                const remainder = number % 1000
                const minutes = Math.floor(remainder / 10)
                const seconds = remainder % 10
                return `${hours.toString().padStart(2, '0')}:${Math.min(minutes, 59).toString().padStart(2, '0')}:${(seconds * 10).toString().padStart(2, '0')}`
            } else if (numStr.length === 6) {
                // Six digits: "143005" -> "14:30:05" (treat as HHMMSS format)
                const hours = Math.floor(number / 10000)
                const remainder = number % 10000
                const minutes = Math.floor(remainder / 100)
                const seconds = remainder % 100
                return `${hours.toString().padStart(2, '0')}:${Math.min(minutes, 59).toString().padStart(2, '0')}:${Math.min(seconds, 59).toString().padStart(2, '0')}`
            } else {
                // More than 6 digits, treat as total minutes
                const totalMinutes = number
                const hours = Math.floor(totalMinutes / 60)
                const minutes = totalMinutes % 60
                return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`
            }
        }
        
        return '00:00:00'
    }

    const handleTimeBlur = async (rowId, day, value) => {
        // Get the original time value before any changes
        const row = timesheetRows.find(r => r.id === rowId)
        if (!row) return

        const originalTime = row.times[day] || '00:00:00'

        // If value is empty or whitespace, keep the original value (no change made)
        // This prevents data loss when user clicks away without typing
        if (!value || value.trim() === '') {
            // Exit editing mode but preserve original value
            setTimesheetRows(timesheetRows.map(r =>
                r.id === rowId
                    ? {
                        ...r,
                        isEditing: { ...r.isEditing, [day]: false },
                        editingValues: { ...r.editingValues, [day]: '' }
                    }
                    : r
            ))
            return // Don't save anything
        }

        // Check if project is selected before allowing time entry
        if (!row.selectedProject) {
            // Alert user and revert to editing mode
            alert('Please select a project before entering time')

            // Exit editing mode and clear the value
            setTimesheetRows(timesheetRows.map(r =>
                r.id === rowId
                    ? {
                        ...r,
                        isEditing: { ...r.isEditing, [day]: false },
                        editingValues: { ...r.editingValues, [day]: '' }
                    }
                    : r
            ))
            return
        }

        // Format the input value
        const formattedTime = formatTimeInput(value)

        // If formatted time is the same as original, no need to update
        if (formattedTime === originalTime) {
            // Exit editing mode without changing the value
            setTimesheetRows(timesheetRows.map(r =>
                r.id === rowId
                    ? {
                        ...r,
                        isEditing: { ...r.isEditing, [day]: false },
                        editingValues: { ...r.editingValues, [day]: '' }
                    }
                    : r
            ))
            return // No change needed
        }

        // Update the time and exit editing mode
        setTimesheetRows(timesheetRows.map(r =>
            r.id === rowId
                ? {
                    ...r,
                    times: { ...r.times, [day]: formattedTime },
                    isEditing: { ...r.isEditing, [day]: false },
                    editingValues: { ...r.editingValues, [day]: '' }
                }
                : r
        ))

        // If time is entered (not 00:00:00), create/update worklog
        if (formattedTime && formattedTime !== '00:00:00') {
            const row = timesheetRows.find(r => r.id === rowId)
            if (!row || !row.selectedProject) {
                return
            }

            // Get the date for the clicked cell
            const weekStart = moment(dateRange.from)
            const dayIndex = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].indexOf(day)
            const cellDate = weekStart.clone().add(dayIndex, 'days')

            // Find existing worklogs for this project/task/date combination
            const matchingWorklogs = worklogData.filter(worklog => {
                const worklogDate = moment(worklog.date)
                const isSameDate = worklogDate.isSame(cellDate, 'day')
                const isSameProject = worklog.project?.id === row.selectedProject?.id
                const isSameTask = row.selectedTask
                    ? worklog.task?.id === row.selectedTask?.id
                    : !worklog.task

                return isSameDate && isSameProject && isSameTask
            })

            // Calculate start and end times based on duration
            const [hours, minutes, seconds] = formattedTime.split(':').map(Number)
            const totalSeconds = hours * 3600 + minutes * 60 + seconds

            apiClient.setAuthToken(apiToken)

            // If there's exactly 1 existing worklog, update it instead of creating a new one
            if (matchingWorklogs.length === 1) {
                const existingWorklog = matchingWorklogs[0]

                // Keep the same start time, but update the end time based on the new duration
                const startTime = moment(existingWorklog.start_time)
                const endTime = startTime.clone().add(totalSeconds, 'seconds')

                const updateData = {
                    description: existingWorklog.description || 'Time entry',
                    start_time: startTime.toISOString(),
                    end_time: endTime.toISOString(),
                    logged_date: cellDate.format('YYYY-MM-DD'),
                    project: row.selectedProject?.id,
                    task: row.selectedTask?.id || null,
                    user_id: user?.id || null,
                    billable: row.billable !== undefined ? row.billable : existingWorklog.billable
                }

                // Update the existing worklog
                apiClient.updateWorklog(existingWorklog.id, updateData)
                    .then(() => {
                        console.log('Worklog updated successfully')
                    })
                    .catch((error) => {
                        console.error('Failed to update worklog:', error)
                        // Revert the local state change on error
                        setTimesheetRows(prevRows => prevRows.map(r =>
                            r.id === rowId
                                ? {
                                    ...r,
                                    times: { ...r.times, [day]: originalTime }
                                }
                                : r
                        ))
                        alert('Failed to save time entry: ' + (error.message || 'Unknown error'))
                    })
            } else {
                // No existing worklogs or multiple worklogs - create a new one
                // Calculate start time based on all worklogs for this day (across all projects)
                // INCLUDING pending worklogs that haven't been refreshed yet
                let startTime

                // Get all worklogs for this day from all projects (existing + pending)
                const allWorklogsForDay = [
                    ...worklogData.filter(w => {
                        const worklogDate = moment(w.start_time)
                        return worklogDate.isSame(cellDate, 'day')
                    }),
                    ...pendingWorklogs.filter(w => {
                        const worklogDate = moment(w.start_time)
                        return worklogDate.isSame(cellDate, 'day')
                    })
                ]

                if (allWorklogsForDay.length > 0) {
                    // Sort all worklogs by end time and get the latest end time
                    const sortedAllWorklogs = allWorklogsForDay.sort((a, b) =>
                        moment(a.end_time).valueOf() - moment(b.end_time).valueOf()
                    )
                    const lastWorklogOfDay = sortedAllWorklogs[sortedAllWorklogs.length - 1]
                    startTime = moment(lastWorklogOfDay.end_time)
                } else {
                    // No existing worklogs for this day, start from 9am
                    startTime = cellDate.clone().hour(9).minute(0).second(0)
                }

                const endTime = startTime.clone().add(totalSeconds, 'seconds')

                const payload = {
                    worklog: {
                        description: 'Time entry',
                        start_time: startTime.toISOString(),
                        end_time: endTime.toISOString(),
                        logged_date: cellDate.format('YYYY-MM-DD'),
                        project: row.selectedProject?.id,
                        task: row.selectedTask?.id || null,
                        user_id: user?.id || null,
                        billable: row.billable !== undefined ? row.billable : true
                    }
                }

                // Add to pending worklogs immediately so subsequent entries can chain properly
                const pendingWorklog = {
                    start_time: startTime.toISOString(),
                    end_time: endTime.toISOString(),
                    project: { id: row.selectedProject?.id },
                    task: row.selectedTask ? { id: row.selectedTask.id } : null
                }
                setPendingWorklogs(prev => [...prev, pendingWorklog])

                // Create a new worklog
                apiClient.createWorklog(payload.worklog)
                    .then(() => {
                        console.log('Worklog created successfully')
                        // Note: We don't remove from pendingWorklogs here
                        // It will be cleared when fetchTimesheetData runs
                    })
                    .catch((error) => {
                        console.error('Failed to create worklog:', error)
                        // Remove from pending worklogs since it failed
                        setPendingWorklogs(prev => prev.filter(w => w !== pendingWorklog))
                        // Revert the local state change on error
                        setTimesheetRows(prevRows => prevRows.map(r =>
                            r.id === rowId
                                ? {
                                    ...r,
                                    times: { ...r.times, [day]: '00:00:00' }
                                }
                                : r
                        ))
                        alert('Failed to save time entry: ' + (error.message || 'Unknown error'))
                    })
            }
        }
    }
    
    const handleTimeFocus = (rowId, day) => {
        // Check if this cell has multiple entries
        const row = timesheetRows.find(r => r.id === rowId)
        if (!row) return

        // If cell has multiple entries, open modal instead of allowing inline editing
        if (hasMultipleEntries(row, day)) {
            handleTimeCellClick(row, day)
            return
        }

        // Handle both click and tab focus - enter editing mode
        setTimesheetRows(timesheetRows.map(row =>
            row.id === rowId
                ? {
                    ...row,
                    isEditing: { ...row.isEditing, [day]: true },
                    editingValues: { ...row.editingValues, [day]: '' }
                }
                : row
        ))
    }
    
    const getDisplayValue = (row, day) => {
        // If editing, show the editing value (starts empty, shows what user types)
        // If not editing, show the formatted time
        return row.isEditing[day] ? (row.editingValues[day] || '') : row.times[day]
    }
    
    const getPlaceholderValue = (row, day) => {
        // Show the current formatted time as placeholder when editing
        return row.isEditing[day] ? row.times[day] : '00:00:00'
    }
    
    const handleTimeKeyDown = (e, rowId, day) => {
        if (e.key === 'Enter' || e.key === 'Tab') {
            e.target.blur() // Trigger the blur event
        }
    }

    const calculateRowTotal = (times) => {
        let totalMinutes = 0
        Object.values(times).forEach(time => {
            const [hours, minutes, seconds] = time.split(':').map(Number)
            totalMinutes += hours * 60 + minutes + seconds / 60
        })
        const hours = Math.floor(totalMinutes / 60)
        const mins = Math.floor(totalMinutes % 60)
        const secs = Math.floor((totalMinutes % 1) * 60)
        return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    }

    const calculateDayTotal = (dayKey) => {
        let totalMinutes = 0
        timesheetRows.forEach(row => {
            const time = row.times[dayKey]
            const [hours, minutes, seconds] = time.split(':').map(Number)
            totalMinutes += hours * 60 + minutes + seconds / 60
        })
        const hours = Math.floor(totalMinutes / 60)
        const mins = Math.floor(totalMinutes % 60)
        const secs = Math.floor((totalMinutes % 1) * 60)
        return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    }

    const calculateGrandTotal = () => {
        let totalMinutes = 0
        timesheetRows.forEach(row => {
            Object.values(row.times).forEach(time => {
                const [hours, minutes, seconds] = time.split(':').map(Number)
                totalMinutes += hours * 60 + minutes + seconds / 60
            })
        })
        const hours = Math.floor(totalMinutes / 60)
        const mins = Math.floor(totalMinutes % 60)
        const secs = Math.floor((totalMinutes % 1) * 60)
        return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    }

    const copyLastWeek = async () => {
        if (!apiToken || !dateRange.from || !dateRange.to) {
            alert('Please select a valid date range first')
            return
        }

        // Show confirmation dialog
        const confirmMessage = `This will copy all worklogs from last week (${getPreviousWeekDateRange()}) to the current week and create new worklog entries in the database. This action cannot be undone.\n\nAre you sure you want to proceed?`

        if (!window.confirm(confirmMessage)) {
            return
        }

        try {
            setCopyingLastWeek(true)
            apiClient.setAuthToken(apiToken)

            // Call backend API to copy worklogs
            const params = {
                target_week_start: dateRange.from,
                target_week_end: dateRange.to
                // Don't pass user_id - backend will use authenticated user
            }

            const response = await apiClient.post('/worklogs/copy_from_last_week', params)

            // Refresh the timesheet data to show the new entries
            await fetchTimesheetData(selectedTeammate?.id)

            const { copied_count, created_worklogs } = response
            const message = copied_count === 1
                ? `Successfully copied 1 worklog from last week`
                : `Successfully copied ${copied_count} worklogs from last week`

            alert(message)

        } catch (error) {
            console.error('Failed to copy last week:', error)
            const errorMessage = error.response?.data?.error || error.message || 'Unknown error occurred'
            alert('Failed to copy last week: ' + errorMessage)
        } finally {
            setCopyingLastWeek(false)
        }
    }

    const getPreviousWeekDateRange = () => {
        if (!dateRange.from || !dateRange.to) return 'previous week'

        const currentWeekStart = moment(dateRange.from)
        const lastWeekStart = currentWeekStart.clone().subtract(7, 'days')
        const lastWeekEnd = moment(dateRange.to).subtract(7, 'days')

        return `${lastWeekStart.format('M/D/YYYY')} - ${lastWeekEnd.format('M/D/YYYY')}`
    }

    const weekDates = getWeekDates()

    return (
        <div className={styles.timesheetContainer}>
            {/* Top Controls */}
            <div className={styles.topControls}>
                <div className={styles.leftControls}>
                    <h2>Timesheet</h2>
                </div>

                <div className={styles.rightControls}>
                    <div className={styles.controlGroup}>
                        <DateRangePicker
                            dateRange={dateRange}
                            onDateRangeChange={setDateRange}
                        />
                    </div>
                </div>
            </div>

            {/* Loading and Error States */}
            {loading && (
                <div className={styles.loadingState}>
                    <p>Loading timesheet data...</p>
                </div>
            )}
            
            {error && (
                <div className={styles.errorState}>
                    <p>Error loading timesheet: {error}</p>
                    <button onClick={() => fetchTimesheetData(selectedTeammate?.id)} className={styles.retryButton}>
                        Retry
                    </button>
                </div>
            )}

            {/* Timesheet Table */}
            <div className={styles.tableContainer}>
                <table className={styles.timesheetTable}>
                    <thead>
                        <tr>
                            <th className={styles.projectHeader}>Projects</th>
                            {weekDates.map((dateInfo, index) => (
                                <th key={index} className={styles.dayHeader}>
                                    <div className={styles.dayName}>{dateInfo.day}, {dateInfo.date}</div>
                                </th>
                            ))}
                            <th className={styles.totalHeader}>Total:</th>
                        </tr>
                    </thead>
                    <tbody>
                        {timesheetRows.map(row => (
                            <tr key={row.id} className={styles.timesheetRow}>
                                <td className={styles.projectCell}>
                                    <div className={styles.projectSelector}>
                                        <button 
                                            className={row.isSelectingProject ? styles.selectProjectButton : styles.selectedProjectButton}
                                            onClick={() => handleOpenProjectModal(row.id)}
                                        >
                                            <span className={styles.addIcon}>+</span>
                                            {getProjectButtonText(row)}
                                        </button>
                                        <button 
                                            className={`${styles.removeButton} ${hasLoggedTime(row) ? styles.removeButtonWithTime : ''}`}
                                            onClick={() => removeRow(row.id)}
                                            title={row.isSelectingProject ? "Remove row" : `Remove ${row.selectedProject?.name || 'project'}`}
                                        >
                                            ×
                                        </button>
                                    </div>
                                </td>
                                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                                    <td key={day} className={styles.timeCell}>
                                        <div className={styles.timeCellWrapper}>
                                            <input
                                                type="text"
                                                className={`${styles.timeInput} ${row.times[day] !== '00:00:00' ? styles.hasTime : ''}`}
                                                value={getDisplayValue(row, day)}
                                                placeholder={getPlaceholderValue(row, day)}
                                                onChange={(e) => updateTime(row.id, day, e.target.value)}
                                                onBlur={(e) => handleTimeBlur(row.id, day, e.target.value)}
                                                onFocus={(e) => {
                                                    handleTimeFocus(row.id, day)
                                                    e.target.select()
                                                }}
                                                onKeyDown={(e) => handleTimeKeyDown(e, row.id, day)}
                                            />
                                            {row.times[day] !== '00:00:00' && (
                                                <button
                                                    className={styles.moreOptionsButton}
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        handleTimeCellClick(row, day)
                                                    }}
                                                    title="View/edit details"
                                                >
                                                    ⋮
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                ))}
                                <td className={styles.totalCell}>
                                    {calculateRowTotal(row.times)}
                                </td>
                            </tr>
                        ))}
                        
                        {/* Total Row */}
                        <tr className={styles.totalRow}>
                            <td className={styles.totalLabel}>Total:</td>
                            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                                <td key={day} className={styles.dayTotal}>
                                    {calculateDayTotal(day)}
                                </td>
                            ))}
                            <td className={styles.grandTotal}>
                                {calculateGrandTotal()}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* Bottom Controls */}
            <div className={styles.bottomControls}>
                <button className={styles.addRowButton} onClick={addNewRow}>
                    <span className={styles.addIcon}>+</span>
                    Add new row
                </button>
                
                <button
                    className={styles.copyWeekButton}
                    onClick={copyLastWeek}
                    disabled={copyingLastWeek || loading}
                >
                    <span className={styles.copyIcon}>📋</span>
                    {copyingLastWeek ? 'Copying...' : 'Copy last week'}
                </button>
            </div>

            {/* Project Task Selection Modal */}
            <ProjectTaskSelectionModal
                isOpen={isProjectModalOpen}
                onClose={handleCloseProjectModal}
                onSelection={handleProjectTaskSelection}
                availableProjects={projects}
                availableTasks={tasks}
                initialSelectedProject={getCurrentRowSelections().project}
                initialSelectedTask={getCurrentRowSelections().task}
                initialBillable={getCurrentRowSelections().billable}
            />

            {/* Add/Edit Worklog Modal */}
            <AddWorklogModal
                isOpen={isWorklogModalOpen}
                onClose={handleCloseWorklogModal}
                onSave={handleSaveWorklog}
                onDelete={handleDeleteWorklog}
                initialData={currentWorklogData}
                isEditMode={isEditingWorklog}
                teammates={teammates}
                isAdmin={isAdmin}
                currentUser={user}
                availableProjects={projects}
                availableTasks={tasks}
                apiToken={apiToken}
            />

            {/* Multiple Worklog Entries Modal */}
            <MultipleWorklogEntriesModal
                isOpen={isMultipleEntriesModalOpen}
                onClose={handleCloseMultipleEntriesModal}
                worklogs={multipleWorklogEntries}
                onSave={handleSaveMultipleEntry}
                onDelete={handleDeleteMultipleEntry}
                projects={projects}
                teammates={teammates}
                currentUser={user}
            />
        </div>
    )
}